// ccp/src/lib/transcriptFeed.js
//
// Phase 3 live transcript + per-role sentiment — the PURE half (no React, no
// fetch), so scripts/verify-transcript-feed.mjs can prove it without a browser.
//
// Rules, as decided 2026-09-10 (04-decisions "afternoon" + "afternoon 2"):
// - one feed (knktr-live-transcript) drives the transcript panel and both
//   sentiment pills;
// - lines are deduplicated by Contact Lens Transcript.Id and kept in offset
//   order, so a refetch of the whole session never flickers;
// - each role's indicator is the Sentiment LABEL of that role's most recent
//   utterance (POSITIVE / NEUTRAL / NEGATIVE) plus a trend arrow = direction
//   of change from that role's PREVIOUS label. No rolling window, no 0–100
//   score. That decision is locked; do not reintroduce a numeric score here.

export const POLL_MS = 3000;
export const SENTIMENT_RANK = { NEGATIVE: 0, NEUTRAL: 1, POSITIVE: 2 };
export const ROLES = ['CUSTOMER', 'AGENT'];

// Merge a fresh full read into the existing list. Known ids keep their object
// identity (stable React keys / no re-render churn) unless the text changed;
// new ids are appended; the result is re-sorted by offset.
export function mergeLines(existing, incoming) {
  const byId = new Map((existing || []).map(l => [l.id, l]));
  let changed = false;
  for (const l of incoming || []) {
    if (!l || !l.id) continue;
    const prev = byId.get(l.id);
    if (!prev) { byId.set(l.id, l); changed = true; }
    else if (prev.text !== l.text || prev.sentiment !== l.sentiment) { byId.set(l.id, { ...prev, ...l }); changed = true; }
  }
  if (!changed) return existing || [];
  return [...byId.values()].sort((a, b) => (a.beginMs - b.beginMs) || String(a.id).localeCompare(String(b.id)));
}

function trendOf(latest, previous) {
  if (!latest || !previous) return null;
  const a = SENTIMENT_RANK[latest], b = SENTIMENT_RANK[previous];
  if (a == null || b == null) return null;
  return a > b ? 'up' : a < b ? 'down' : 'flat';
}

// { customer: { label, previous, trend, at }, agent: { ... } } — each null
// when that role has no labelled utterance yet. Unlabelled lines are skipped,
// never treated as NEUTRAL.
export function roleSentiment(lines) {
  const out = { customer: null, agent: null };
  for (const role of ROLES) {
    const labelled = (lines || []).filter(l => l && l.role === role && typeof l.sentiment === 'string' && SENTIMENT_RANK[l.sentiment] != null);
    if (!labelled.length) continue;
    const latest = labelled[labelled.length - 1];
    const previous = labelled.length > 1 ? labelled[labelled.length - 2] : null;
    out[role === 'CUSTOMER' ? 'customer' : 'agent'] = {
      label: latest.sentiment,
      previous: previous?.sentiment ?? null,
      trend: trendOf(latest.sentiment, previous?.sentiment),
      at: latest.time ?? null,
    };
  }
  return out;
}

// Demo mode seeds the feed from the mock contact's transcript so the surfaces
// render without Connect. Mock lines use the console's speaker tags.
export function linesFromMock(transcript) {
  const SPEAKER_ROLE = { Cust: 'CUSTOMER', You: 'AGENT', AI: 'AI' };
  return (transcript || []).map((l, i) => ({
    id: `mock-${i}`,
    role: SPEAKER_ROLE[l.speaker] || 'AI',
    speaker: l.speaker,
    text: l.text,
    beginMs: i * 7000,
    endMs: i * 7000 + 5000,
    time: l.time,
    sentiment: l.sentiment ?? null,
    entities: l.entities ?? [],
  }));
}

// #127 (2026-09-22): what the transcript provider should do with the feed it
// holds, given where the contact is. Before this, the feed was reset only when
// a NEW contact appeared, so the last call's lines and sentiment sat in context
// on the idle screen until the next call connected.
//   'track' — a new contact is connected: start fresh for it.
//   'clear' — the contact has gone and wrap-up is not active: drop the feed.
//   null    — nothing to do (same contact; or wrap-up, where the feed is kept
//             for the single final read and the wrap-up screen).
export function feedResetFor({ contactId, trackedId, acwActive }) {
  if (contactId && contactId !== trackedId) return 'track';
  if (!contactId && !acwActive && trackedId) return 'clear';
  return null;
}
